<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { vOnClickOutside } from '@vueuse/components';
import { useTrack } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';
import NextButton from 'next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    NextButton,
    NextInput,
  },
  directives: {
    onClickOutside: vOnClickOutside,
  },
  props: {
    filterType: {
      type: Number,
      default: 0,
    },
    customViewsQuery: {
      type: Object,
      default: () => {},
    },
    openLastSavedItem: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['close'],
  setup() {
    const { isAdmin } = useAdmin();
    const { setPersonalFavorite, setAccountFavorite } = useKaptionFavorites();

    return {
      v$: useVuelidate(),
      isAdmin,
      setPersonalFavorite,
      setAccountFavorite,
    };
  },
  data() {
    return {
      show: true,
      name: '',
      favoriteForMe: false,
      favoriteForAccount: false,
    };
  },

  computed: {
    isButtonDisabled() {
      return this.v$.name.$invalid;
    },
  },

  validations: {
    name: {
      required,
      minLength: minLength(1),
    },
  },

  methods: {
    onClose() {
      this.$emit('close');
    },
    async saveCustomViews() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      try {
        const response = await this.$store.dispatch('customViews/create', {
          name: this.name,
          filter_type: this.filterType,
          query: this.customViewsQuery,
          account_favorite: this.isAdmin ? this.favoriteForAccount : false,
        });

        const createdId = response?.data?.id;
        if (this.filterType === 0 && createdId) {
          if (this.favoriteForMe) {
            this.setPersonalFavorite('folder', createdId, true);
          }
          if (this.isAdmin && this.favoriteForAccount) {
            await this.setAccountFavorite('folder', createdId, true);
          }
        }
        this.alertMessage =
          this.filterType === 0
            ? this.$t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.SUCCESS_MESSAGE')
            : this.$t('FILTER.CUSTOM_VIEWS.ADD.API_SEGMENTS.SUCCESS_MESSAGE');
        this.onClose();

        useTrack(CONTACTS_EVENTS.SAVE_FILTER, {
          type: this.filterType === 0 ? 'folder' : 'segment',
        });
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.filterType === 0
            ? errorMessage
            : this.$t('FILTER.CUSTOM_VIEWS.ADD.API_SEGMENTS.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
      this.openLastSavedItem();
    },
  },
};
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - rounded-3xl for rounder appearance -->
  <div
    v-on-click-outside="[
      () => $emit('close'),
      { ignore: ['#saveFilterTeleportTarget'] },
    ]"
    class="z-40 max-w-3xl lg:w-[500px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-3xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ $t('FILTER.CUSTOM_VIEWS.ADD.TITLE') }}
    </h3>
    <form class="w-full grid gap-6" @submit.prevent="saveCustomViews">
      <NextInput
        v-model="name"
        :placeholder="$t('FILTER.CUSTOM_VIEWS.ADD.PLACEHOLDER')"
        :message="v$.name.$error && $t('FILTER.CUSTOM_VIEWS.ADD.ERROR_MESSAGE')"
        :message-type="v$.name.$error && 'error'"
        @blur="v$.name.$touch"
      />
      <div class="grid gap-2">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            v-model="favoriteForMe"
            type="checkbox"
            class="m-0 border border-n-weak rounded-md h-4 w-4"
          />
          <span>{{ $t('FILTER.CUSTOM_VIEWS.ADD.FAVORITE_FOR_ME') }}</span>
        </label>
        <label
          v-if="isAdmin"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input
            v-model="favoriteForAccount"
            type="checkbox"
            class="m-0 border border-n-weak rounded-md h-4 w-4"
          />
          <span>{{ $t('FILTER.CUSTOM_VIEWS.ADD.FAVORITE_FOR_ACCOUNT') }}</span>
        </label>
      </div>
      <div class="flex flex-row justify-end w-full gap-2">
        <NextButton faded slate sm @click.prevent="onClose">
          {{ $t('FILTER.CUSTOM_VIEWS.ADD.CANCEL_BUTTON') }}
        </NextButton>
        <NextButton solid blue sm :disabled="isButtonDisabled">
          {{ $t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON') }}
        </NextButton>
      </div>
    </form>
  </div>
</template>
